import { coerceInt, validateConfig, validateImageFile } from "../core/validation.js";
import { buildCropFromCenter, buildInitialCrop } from "../core/cropMath.js";
import { getGameById } from "../core/games.js";
import { SCALING_MODES, normalizeScalingMode } from "../core/scaling.js";
import { detectCanvasSupport } from "../processing/canvasSupport.js";

async function decodePreviewImage(file) {
    const support = detectCanvasSupport();

    if (support.imageBitmapSupported) {
        try {
            return await createImageBitmap(file);
        } catch (_) {
            // Fallback to HTMLImageElement decode path for environments where
            // createImageBitmap exists but cannot decode the provided file.
        }
    }

    const objectUrl = URL.createObjectURL(file);
    try {
        const image = await new Promise((resolve, reject) => {
            const next = new Image();
            next.onload = () => resolve(next);
            next.onerror = () => reject(new Error("Image decode failed."));
            next.src = objectUrl;
        });
        return image;
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

export function bindFormHandlers({ refs, store, pipeline }) {
    function syncTileResolutionInputs(value) {
        refs.tileResolutionInput.value = String(value);
        if (refs.tileResolutionQuickInput) {
            refs.tileResolutionQuickInput.value = String(value);
        }
    }

    refs.form.addEventListener("submit", async (event) => {
        event.preventDefault();
        const state = store.getState();
        const fileError = validateImageFile(state.imageFile);

        if (fileError) {
            store.setState({ workflow: "error", statusMessage: fileError });
            return;
        }

        const validation = validateConfig(state.config);
        if (!validation.isValid) {
            store.setState({ workflow: "error", statusMessage: validation.errors.join(" ") });
            return;
        }

        try {
            store.setState({ workflow: "validating", statusMessage: "Preparing generation...", progressPercent: 0 });
            await pipeline.startGeneration();
        } catch (error) {
            store.setState({ workflow: "error", statusMessage: error.message || "Generation failed." });
        }
    });

    refs.cancelButton.addEventListener("click", () => {
        pipeline.cancelActiveJob();
        store.setState({ workflow: "ready", statusMessage: "Generation cancelled." });
    });

    refs.imageInput.addEventListener("change", async (event) => {
        const file = event.target.files?.[0] ?? null;
        store.setState({ imageFile: file });

        if (!file) {
            store.setState({ workflow: "empty", statusMessage: "No image selected.", imageBitmap: null, imageMeta: null, crop: null });
            return;
        }

        const fileError = validateImageFile(file);
        if (fileError) {
            store.setState({ workflow: "error", statusMessage: fileError, imageBitmap: null, imageMeta: null, crop: null });
            return;
        }

        let bitmap;
        try {
            bitmap = await decodePreviewImage(file);
        } catch (error) {
            store.setState({
                workflow: "error",
                statusMessage: "Unable to decode the selected image.",
                imageBitmap: null,
                imageMeta: null,
                crop: null
            });
            return;
        }

        const imageMeta = {
            widthPx: bitmap.naturalWidth ?? bitmap.width,
            heightPx: bitmap.naturalHeight ?? bitmap.height,
            mimeType: file.type,
            fileName: file.name,
            byteSize: file.size
        };

        const config = store.getState().config;
        const crop = buildInitialCrop(imageMeta.widthPx, imageMeta.heightPx, config.tilesWide, config.tilesHigh);

        store.setState({
            workflow: "ready",
            statusMessage: "Image loaded. Configure and generate.",
            imageBitmap: bitmap,
            imageMeta,
            crop
        });
    });

    refs.gameSelect.addEventListener("change", () => {
        const gameId = refs.gameSelect.value;
        const nextConfig = { ...store.getState().config, gameId };

        store.setState({
            config: nextConfig,
            statusMessage: `Target game set to ${getGameById(gameId).label}.`
        });
    });

    function onDimensionsChange() {
        const state = store.getState();
        const tileResolution = coerceInt(refs.tileResolutionInput.value, 256);
        const nextConfig = {
            ...state.config,
            tilesWide: coerceInt(refs.tilesWideInput.value, 1),
            tilesHigh: coerceInt(refs.tilesHighInput.value, 1),
            tileResolution,
            scalingMode: normalizeScalingMode(state.config.scalingMode)
        };

        let nextCrop = state.crop;
        const ratioChanged = nextConfig.tilesWide !== state.config.tilesWide || nextConfig.tilesHigh !== state.config.tilesHigh;
        if (state.imageMeta && ratioChanged) {
            nextCrop = buildCropFromCenter(
                state.imageMeta.widthPx,
                state.imageMeta.heightPx,
                nextConfig.tilesWide,
                nextConfig.tilesHigh,
                state.crop
            );
        }

        const validation = validateConfig(nextConfig);
        const nextStatusMessage = validation.isValid
            ? (state.imageBitmap ? "Image loaded. Configure and generate." : "No image selected.")
            : validation.errors.join(" ");

        store.setState({
            config: nextConfig,
            crop: nextCrop,
            workflow: validation.isValid && state.imageBitmap ? "ready" : state.workflow,
            statusMessage: nextStatusMessage
        });
    }

    function onTileResolutionExactInput() {
        syncTileResolutionInputs(refs.tileResolutionInput.value);
        onDimensionsChange();
    }

    function onTileResolutionQuickInput() {
        if (!refs.tileResolutionQuickInput) {
            return;
        }

        syncTileResolutionInputs(refs.tileResolutionQuickInput.value);
        onDimensionsChange();
    }

    refs.tilesWideInput.addEventListener("input", onDimensionsChange);
    refs.tilesHighInput.addEventListener("input", onDimensionsChange);
    refs.tileResolutionInput.addEventListener("input", onTileResolutionExactInput);
    if (refs.tileResolutionQuickInput) {
        refs.tileResolutionQuickInput.addEventListener("input", onTileResolutionQuickInput);
    }

    refs.scalingModeButtons?.forEach((button) => {
        button.addEventListener("click", () => {
            const nextMode = normalizeScalingMode(button.dataset.scalingMode);
            if (![SCALING_MODES.fit, SCALING_MODES.enhance].includes(nextMode)) {
                return;
            }

            const state = store.getState();
            if (nextMode === state.config.scalingMode) {
                return;
            }

            store.setState({
                config: {
                    ...state.config,
                    scalingMode: nextMode
                },
                statusMessage: `Scaling mode set to ${nextMode === SCALING_MODES.fit ? "Fit to Image" : "Enhance to Target"}.`
            });
        });
    });
}
